import { ADDRESS_FORMATS, type AddressFormat } from './addressFormats.js';

export type Address = {
  name?: string;
  company?: string;
  address1?: string;
  address2?: string;
  postalCode?: string;
  city?: string;
  province?: string;
  country?: string;
};

const TEMPLATE_PATTERN = /({.?.?\w+.?.?})/g;
const NOT_ALPHANUMERIC = /[^a-z0-9]/gi;

export const renderAddressTemplate = ({
  address,
  template,
}: {
  address: Address;
  template: string;
}) => {
  const replacementKeys = template.match(TEMPLATE_PATTERN);
  if (!replacementKeys) return '';

  const { hydratedTemplate } = replacementKeys.reduce(
    (acc, field) => {
      if (acc.isMissingField) {
        return acc;
      }
      const addressKey = field.replace(NOT_ALPHANUMERIC, '') as keyof Address;

      // Cut off the rest of the template if the field is missing, address2 is excluded from this check
      const isMissingField = !address[addressKey] && addressKey !== 'address2';
      if (isMissingField) {
        const cutoffIndex = acc.hydratedTemplate.indexOf(field);
        return {
          hydratedTemplate:
            cutoffIndex !== -1
              ? acc.hydratedTemplate.substring(0, cutoffIndex)
              : acc.hydratedTemplate,
          isMissingField: true,
        };
      }

      const replacementValue = address[addressKey]
        ? field
            .replace('{', '')
            .replace('}', '')
            .replace(addressKey, address[addressKey])
        : '';

      return {
        hydratedTemplate: acc.hydratedTemplate.replace(field, replacementValue),
        isMissingField: acc.isMissingField,
      };
    },
    { hydratedTemplate: template, isMissingField: false },
  );

  return hydratedTemplate
    .replace(/,\s*$/, '')
    .replace(/,\s*,/g, ',')
    .replace(/\s+/g, ' ')
    .trim();
};

export const formatAddressMultiLine = ({
  countryCode,
  address,
}: {
  countryCode: string;
  address: Address;
}) => {
  const addressFormat = (ADDRESS_FORMATS[countryCode] || ADDRESS_FORMATS.US)
    ?.display?.multi;
  if (!addressFormat) return [];
  return addressFormat
    .map((template) => renderAddressTemplate({ address, template }))
    .filter(Boolean);
};

export const formatAddressSingleLine = ({
  countryCode,
  address,
}: {
  countryCode: string;
  address: Address;
}) => {
  const addressFormat = (ADDRESS_FORMATS[countryCode] || ADDRESS_FORMATS.US)
    ?.display?.single;
  if (!addressFormat) return '';
  return renderAddressTemplate({ address, template: addressFormat });
};

export const getAddressForm = (countryCode: string): AddressFormat['form'] => {
  const countryForm = ADDRESS_FORMATS[countryCode]?.form;
  const fallbackForm = ADDRESS_FORMATS.US?.form as AddressFormat['form'];
  return countryForm || fallbackForm;
};
